import { FC } from "react"
import { RateFlowDetails } from "../../../../../../../../types"
import {
  ConfigAdminNumberInput,
  ConfigAdminPanelStackedField,
  ConfigAdminToggleRow
} from "../../../../../config-admin-form-primitives"
import { ConfigAdminPanel } from "../../../../../config-admin-panel"

interface Props {
  values: RateFlowDetails
  setFormField: (name: string, value: any) => void
}

const RateflowGeneral: FC<Props> = ({ values, setFormField }) => {
  const updateSetting = (field: string, value: any) => {
    setFormField("settings", {
      ...values.settings,
      [field]: value
    })
  }

  return (
    <div className="space-y-6 pt-2">
      <ConfigAdminPanel>
        <ConfigAdminPanelStackedField
          label="Target Price"
          fieldKey="target_price"
          hint="Target price between 0-100 used for filtering rate results. Default is 100."
          htmlFor="rateflow-target-price"
        >
          <ConfigAdminNumberInput
            id="rateflow-target-price"
            value={values.settings?.target_price}
            onChange={v => updateSetting("target_price", v ?? 0)}
            parse="float"
            clamp={v => Math.min(Math.max(v, 0), 100)}
            step={0.1}
            min={0}
            max={100}
          />
        </ConfigAdminPanelStackedField>

        <ConfigAdminPanelStackedField
          label="Cache TTL (hours)"
          fieldKey="cache_ttl"
          hint="Duration for how long rate quote responses should be cached. Default is 6 hours."
          htmlFor="rateflow-cache-ttl"
        >
          <ConfigAdminNumberInput
            id="rateflow-cache-ttl"
            value={
              values.settings?.cache_ttl
                ? values.settings.cache_ttl / 3600
                : undefined
            }
            onChange={v => updateSetting("cache_ttl", v ? v * 3600 : 0)}
            parse="float"
            min={0}
            step={0.5}
          />
        </ConfigAdminPanelStackedField>

        <ConfigAdminToggleRow
          label="Default Fees In"
          fieldKey="default_fees_in"
          description="Determines if fees should be included in the loan amount by default. Default is true."
          enabled={values.settings?.default_fees_in ?? true}
          onChange={(value: boolean) => updateSetting("default_fees_in", value)}
          last={false}
        />

        <ConfigAdminToggleRow
          label="Testing Mode"
          fieldKey="testing"
          description="Indicate if the environment is in testing mode. Default is false."
          enabled={values.settings?.testing ?? false}
          onChange={(value: boolean) => updateSetting("testing", value)}
          last={false}
        />

        <ConfigAdminPanelStackedField
          label="Prepaid Interest Days"
          fieldKey="prepaid_interest_days"
          hint="Number of prepaid interest days included in pricing calculations. Default is 5."
          htmlFor="rateflow-prepaid-days"
        >
          <ConfigAdminNumberInput
            id="rateflow-prepaid-days"
            value={values.settings?.prepaid_interest_days}
            onChange={v => updateSetting("prepaid_interest_days", v ?? 0)}
            parse="int"
            min={0}
          />
          {values.id && values.settings?.prepaid_interest_days == null && (
            <p className="mt-1.5 text-xs text-red-500">
              ⚠ No value saved. A default value may be applied.
            </p>
          )}
        </ConfigAdminPanelStackedField>

        <ConfigAdminPanelStackedField
          label="Lead Source"
          fieldKey="lead_source"
          hint="Integer value to identify the source of the lead for tracking and reporting"
          htmlFor="rateflow-lead-source"
          dividerBottom={false}
        >
          <ConfigAdminNumberInput
            id="rateflow-lead-source"
            value={values.settings?.lead_source}
            onChange={v => updateSetting("lead_source", v)}
            parse="int"
            nullable
          />
        </ConfigAdminPanelStackedField>
      </ConfigAdminPanel>
    </div>
  )
}

export default RateflowGeneral
